import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Injectable } from '@angular/core';
import { filter, map, Observable } from 'rxjs';
import { PageAccesses } from './page-accesses.model';
import { PageAccessesService } from '../services/page-access/page-access.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private readonly pageAccessService: PageAccessesService, private readonly router: Router) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean> | boolean {
    return this.pageAccessService.usersPageAccesses$.pipe(
      filter((access: PageAccesses) => {
        return access !== null;
      }),
      map((accesses: PageAccesses) => {
        const navigationTargetUrl = state.url;
        if (navigationTargetUrl.startsWith('/root')) {
          if (navigationTargetUrl.includes('general-stb-config-editor')) {
            return this.handleGeneralConfigEditorScreen(accesses);
          } else {
            return true;
          }
        } else {
          if (navigationTargetUrl.includes('pages')) {
            return this.handlePagesScreen(accesses);
          } else if (navigationTargetUrl.includes('content-groups')) {
            return this.handleContentGroupsScreen(accesses);
          } else if (navigationTargetUrl.includes('menu-editor')) {
            return this.handleMenuEditorScreen(accesses);
          } else if (route.routeConfig.path.includes('application-list-and-metadata')) {
            return this.handleApplicationListAndMetadataScreen(accesses);
          } else if (route.routeConfig.path.includes('brand-settings')) {
            return this.handleBrandSettingsScreen(accesses);
          } else if (route.routeConfig.path.includes('channel-list')) {
            return this.handleChannelListScreen(accesses);
          } else if (route.routeConfig.path.includes('config-editor')) {
            return this.handleConfigEditorScreen(accesses);
          } else {
            return true;
          }
        }
      })
    );
  }

  private handleBrandSettingsScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.brandSettings) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private handleApplicationListAndMetadataScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.applicationListAndMetadata) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private handleChannelListScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.channelList) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private handleConfigEditorScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.configEditor) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private handleGeneralConfigEditorScreen(accesses: PageAccesses): boolean {
    if (accesses.tenantIndependentMenuItem.generalStbConfigEditor) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToTenantIndependentDefault(accesses);
    }
  }

  private handleMenuEditorScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.menuEditor) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private handleContentGroupsScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.contentGroups) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private handlePagesScreen(accesses: PageAccesses): boolean {
    if (accesses.menuItem.launcher) {
      return true;
    } else {
      console.error('Access denied!');
      this.routeToDefault(accesses);
    }
  }

  private routeToDefault(accesses: PageAccesses) {
    const firstAvailableMenuItem = Object.keys(accesses.menuItem).find((i) => accesses.menuItem[i] === true);
    if (firstAvailableMenuItem !== undefined) {
      switch (firstAvailableMenuItem) {
        case 'launcher':
          this.router.navigate(['pages']);
          break;
        case 'contentGroups':
          this.router.navigate(['content-groups']);
          break;
        case 'menuEditor':
          this.router.navigate(['menu-editor']);
          break;
        case 'applicationListAndMetadata':
          this.router.navigate(['application-list-and-metadata']);
          break;
        case 'brandSettings':
          this.router.navigate(['brand-settings']);
          break;
        case 'channelList':
          this.router.navigate(['channel-list']);
          break;
        case 'configEditor':
          this.router.navigate(['config-editor']);
          break;
      }
    } else {
      return this.router.navigate(['/no-permission']);
    }
  }

  private routeToTenantIndependentDefault(accesses: PageAccesses) {
    const firstAvailableMenuItem = Object.keys(accesses.tenantIndependentMenuItem).find(
      (i) => accesses.tenantIndependentMenuItem[i] === true
    );
    if (firstAvailableMenuItem !== undefined) {
      switch (firstAvailableMenuItem) {
        case 'generalConfigEditor':
          this.router.navigate(['/root/general-stb-config-editor']);
          break;
      }
    } else {
      return this.router.navigate(['/root/no-permission']);
    }
  }
}
